import { useState, useEffect } from "react";
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Store,
  Package,
  FolderOpen,
  FileText,
  LogOut,
  RefreshCw,
  Menu,
  X,
  DollarSign,
  Palette,
  CreditCard,
  ShoppingCart,
  User,
  Settings,
  ChevronDown,
  Heart,
  Save,
  Loader2,
  Lock,
  Mail,
  Eye,
  EyeOff,
  PanelTop,
  Upload,
  Users,
  List,
  Star,
  Images,
  Globe,
  Percent,
  Smartphone,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { usePlatformContext } from "../../context/PlatformContext";

// Admin Pages
import AdminOverview from "./AdminOverview";
import AdminCategories from "./AdminCategories";
import AdminProducts from "./AdminProducts";
import AdminCatalog from "./AdminCatalog";
import AdminResellers from "./AdminResellers";
import AdminRequests from "./AdminRequests";
import AdminEarnings from "./AdminEarnings";
import AdminPayouts from "./AdminPayouts";
import AdminOwnerLedger from "./AdminOwnerLedger";
import AdminOrders from "./AdminOrders";
import AdminThemes from "./AdminThemes";
import AdminSettings from "./AdminSettings";
import AdminStoreAppearance from "./AdminStoreAppearance";
import AdminImport from "./AdminImport";
import AdminDistributors from "./AdminDistributors";
import AdminCustomizations from "./AdminCustomizations";
import AdminCollections from "./AdminCollections";
import AdminCustomers from "./AdminCustomers";
import AdminContentPages from "./AdminContentPages";
import AdminWebsiteContent from "./AdminWebsiteContent";
import AdminContentPageEditor from "./AdminContentPageEditor";
import AdminFuneralStoreProducts from "./AdminFuneralStoreProducts";
import AdminFuneralStoreCollections from "./AdminFuneralStoreCollections";
import AdminFeaturedCollections from "./AdminFeaturedCollections";
import ProductManagementProducts from "./ProductManagementProducts";
import ProductManagementCollections from "./ProductManagementCollections";

// New Reseller Pages
import AdminResellerProducts from "./AdminResellerProducts";
import AdminResellerCollections from "./AdminResellerCollections";
import AdminResellerCategories from "./AdminResellerCategories";
import AdminInfinityCases from "./AdminInfinityCases";
import AdminInfinityOrders from "./AdminInfinityOrders";
import AdminInfinityLoginSlider from "./AdminInfinityLoginSlider";
import AdminInfinityAppVersion from "./AdminInfinityAppVersion";
import AdminInfinityPriceMultiplier from "./AdminInfinityPriceMultiplier";
import OpsDashboard from "./OpsDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// What an ops user (operations login) sees: the orders dashboard and the orders list -
// nothing else. The API refuses every other admin endpoint for them as well.
const opsNavGroups = [
  {
    title: "Operations",
    items: [
      { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
      { path: "/admin/orders", icon: ShoppingCart, label: "Orders" },
    ],
  },
];

const navGroups = [
  {
    title: "Dashboard",
    items: [
      { path: "/admin", icon: LayoutDashboard, label: "Overview", exact: true },
    ]
  },
  {
    title: "Financials",
    items: [
      { path: "/admin/orders", icon: ShoppingCart, label: "Orders" },
      { path: "/admin/earnings", icon: DollarSign, label: "Earnings" },
      { path: "/admin/payouts", icon: CreditCard, label: "Payouts" },
    ]
  },
  {
    title: "User Management",
    items: [
      { path: "/admin/resellers", icon: Store, label: "Resellers" },
      { path: "/admin/distributors", icon: Users, label: "Distributors" },
      { path: "/admin/customers", icon: User, label: "Customers" },
      { path: "/admin/requests", icon: FileText, label: "Applications" },
    ]
  },
  {
    title: "Product Management",
    items: [
      { path: "/admin/product-management/collections", icon: List, label: "Collections" },
      { path: "/admin/product-management/products", icon: Package, label: "Products" },
    ]
  },
  {
    // The infinitytouch.app marketing site lives here too — it is Infinity
    // Touch's own CMS, as distinct from "E-Displayroom", which is
    // eDisplayroom's shop chrome.
    title: "Infinity Touch",
    items: [
      { path: "/admin/infinity-cases", icon: Heart, label: "Cases" },
      { path: "/admin/infinity-orders", icon: ShoppingCart, label: "Orders" },
      { path: "/admin/infinity-price-multiplier", icon: Percent, label: "Price Multiplier" },
      { path: "/admin/infinity-login-slider", icon: Images, label: "Customer Login Slider" },
      { path: "/admin/infinity-app-version", icon: Smartphone, label: "App Version" },
      { path: "/admin/website-content", icon: Globe, label: "Website Content" },
      { path: "/admin/pages", icon: FileText, label: "Website Pages" },
    ]
  },
  {
    title: "Reseller",
    items: [
      { path: "/admin/reseller-products", icon: Package, label: "Products" },
      { path: "/admin/reseller-collections", icon: List, label: "Collections" },
    ]
  },
  {
    title: "E-Displayroom",
    items: [
      { path: "/admin/appearance", icon: PanelTop, label: "Appearance" },
      { path: "/admin/featured-collections", icon: Star, label: "Featured Collections" },
      { path: "/admin/pages", icon: FileText, label: "Pages" },
      { path: "/admin/themes", icon: Palette, label: "Themes" },
      { path: "/admin/customizations", icon: List, label: "Customizations" },
    ]
  },
  {
    title: "Settings",
    items: [
      { path: "/admin/settings", icon: Settings, label: "Settings" },
    ]
  }
];

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // State for collapsible sections
  // All nav groups collapsed by default.
  const [openSections, setOpenSections] = useState([]);

  // Profile edit state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', email: '', password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Platform Info & Appearance
  const { platformInfo } = usePlatformContext();
  const [storeAppearance, setStoreAppearance] = useState(null);

  const fetchPlatformData = async () => {
    try {
      const [appearanceRes] = await Promise.all([
        axios.get(`${API}/public/store-appearance`)
      ]);
      setStoreAppearance(appearanceRes.data);

      const appData = appearanceRes.data;

      // Inject primary color into CSS variables
      if (appData?.primary_color) {
        document.documentElement.style.setProperty('--primary', hexToHSL(appData.primary_color));
        document.documentElement.style.setProperty('--ring', hexToHSL(appData.primary_color));
      }

      // Inject font family
      if (appData?.font) {
        // Load font dynamically if not standard
        if (["Inter", "Roboto", "Outfit", "Playfair Display", "Lora", "Cormorant Garamond"].includes(appData.font)) {
          const fontId = `dynamic-font-${appData.font.replace(/\s+/g, '-')}`;
          if (!document.getElementById(fontId)) {
            const link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${appData.font.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;
            document.head.appendChild(link);
          }
        }
        document.body.style.fontFamily = `'${appData.font}', -apple-system, BlinkMacSystemFont, sans-serif`;
      }
    } catch (error) {
      console.error("Failed to fetch platform data:", error);
    }
  };

  // Helper to convert hex to HSL for Shadcn/Tailwind variables
  const hexToHSL = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
      r = "0x" + hex[1] + hex[1];
      g = "0x" + hex[2] + hex[2];
      b = "0x" + hex[3] + hex[3];
    } else if (hex.length === 7) {
      r = "0x" + hex[1] + hex[2];
      g = "0x" + hex[3] + hex[4];
      b = "0x" + hex[5] + hex[6];
    }
    r /= 255; g /= 255; b /= 255;
    let cmin = Math.min(r, g, b), cmax = Math.max(r, g, b), delta = cmax - cmin, h = 0, s = 0, l = 0;
    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    l = (cmax + cmin) / 2;
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);
    return `${h} ${s}% ${l}%`;
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        password: ''
      });
    } catch (error) {
      navigate('/login');
    }
  };

  useEffect(() => {
    fetchUser();
    fetchPlatformData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('session_token');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('session_token');
      navigate('/login');
    }
  };

  const openProfileModal = async () => {
    try {
      // Fetch full user details including password
      const response = await axios.get(`${API}/admin/users/${user?.user_id}`);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        password: response.data.password || ''
      });
      setShowProfileModal(true);
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/admin/users/${user?.user_id}`, profileData);
      toast.success('Profile updated successfully');
      setShowProfileModal(false);
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSyncShopify = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/sync-shopify`);
      toast.success(`Synced ${response.data.categories_synced} categories and ${response.data.products_synced} products`);
    } catch (error) {
      toast.error("Failed to sync Shopify data");
    } finally {
      setSyncing(false);
    }
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const toggleSection = (title) => {
    setOpenSections(prev =>
      prev.includes(title)
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const isOps = user?.role === 'ops';
  const portalName = isOps ? 'Operations Portal' : 'Admin Portal';
  const visibleNavGroups = isOps ? opsNavGroups : navGroups;

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-primary/5" data-testid="admin-dashboard">

      {/* Sidebar */}
      <aside className={`absolute lg:relative z-40 w-64 h-full bg-white/90 backdrop-blur-xl border-r border-primary/20 flex flex-col transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>

        {/* Logo Area */}
        <div className="p-6 border-b border-primary/20 bg-gradient-to-r from-primary/5 to-transparent flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-3">
            {platformInfo.platform_logo_url ? (
              <img
                src={getImageUrl(platformInfo.platform_logo_url)}
                alt={platformInfo.platform_name}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20">
                <Store className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <span className="text-lg font-bold text-slate-900 leading-tight block">
                {platformInfo.platform_name || "eDisplayroom"}
              </span>
              <span className="block text-[10px] text-slate-500 -mt-0.5">by Infinity Funeral Supply</span>
              <span className="block text-xs text-primary font-medium mt-0.5">{portalName}</span>
            </div>
          </Link>
        </div>

        {/* {platformInfo.shopify_sync_enabled && (
          <div className="p-4 flex-shrink-0 border-b border-primary/20">
            <Button
              onClick={handleSyncShopify}
              disabled={syncing}
              className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white rounded-xl shadow-lg shadow-primary/20"
              data-testid="sync-shopify-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Products'}
            </Button>
            {platformInfo.last_shopify_sync && (
              <div className="mt-2 text-[10px] text-slate-500 text-center flex flex-col items-center gap-0.5">
                <span className="font-medium uppercase tracking-wider text-slate-400">Last Synced</span>
                <span className="bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100">
                  {new Date(platformInfo.last_shopify_sync).toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        )} */}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto scrollbar-hide">
          {visibleNavGroups.map((group) => {
            // The ops menu is a single short group - keep it open.
            const isOpen = isOps || openSections.includes(group.title);
            // Check if any item in this group is active
            const hasActiveChild = group.items.some(item => isActive(item.path, item.exact));

            return (
              <Collapsible
                key={group.title}
                open={isOpen}
                onOpenChange={() => toggleSection(group.title)}
                className="space-y-1"
              >
                <CollapsibleTrigger asChild>
                  <button className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all text-xs font-semibold uppercase tracking-wider ${hasActiveChild ? 'text-primary bg-primary/10' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}>
                    {group.title}
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-1 pt-1 ml-1 pl-2 border-l border-primary/20">
                  {group.items.map(item => {
                    const active = isActive(item.path, item.exact);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${active
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className={`w-4 h-4 ${active ? 'text-primary' : 'text-slate-400'}`} />
                        {item.label}
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="absolute inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full min-w-0 bg-transparent">
        {/* Fixed Header within Main Area */}
        <header className="flex-shrink-0 h-[89px] bg-white/90 backdrop-blur-md border-b border-primary/20 flex items-center justify-between px-4 lg:px-8 z-30">
          <div className="flex items-center">
            <button
              className="lg:hidden p-2 mr-4 -ml-2 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              {sidebarOpen ? <X className="w-6 h-6 text-slate-600" /> : <Menu className="w-6 h-6 text-slate-600" />}
            </button>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">
              {portalName}
            </h1>
          </div>

          {/* User Info & Logout */}
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden sm:flex items-center gap-3">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-semibold text-slate-900 leading-tight">{user.name || "Admin User"}</p>
                  <p className="text-xs text-slate-500 leading-tight">{user.email}</p>
                </div>
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-primary/10" />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-primary font-bold text-sm">
                      {user.name?.charAt(0) || 'A'}
                    </span>
                  </div>
                )}
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 rounded-full border-slate-200">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="hidden sm:inline text-slate-700">Account</span>
                  <ChevronDown className="w-3 h-3 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {/* Profile editing uses admin-only user endpoints. */}
                {!isOps && (
                  <>
                    <DropdownMenuItem onClick={openProfileModal}>
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Scrollable Page Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          {/* Nothing mounts until we know who is signed in, so an ops user never opens an
              admin page (or fires its admin-only requests) even for a moment. */}
          {!user ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : isOps ? (
            <Routes>
              <Route index element={<OpsDashboard />} />
              <Route path="orders" element={<AdminOrders role="ops" />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          ) : (
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="resellers" element={<AdminResellers />} />
            <Route path="distributors" element={<AdminDistributors />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="import" element={<AdminImport />} />
            <Route path="appearance" element={<AdminStoreAppearance />} />
            <Route path="themes" element={<AdminThemes />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="catalog" element={<AdminCatalog />} />
            <Route path="collections" element={<AdminCollections />} />
            <Route path="product-management/products" element={<ProductManagementProducts />} />
            <Route path="product-management/collections" element={<ProductManagementCollections />} />
            <Route path="featured-collections" element={<AdminFeaturedCollections />} />
            <Route path="earnings" element={<AdminEarnings />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="payouts/ledger/:kind/:ownerId" element={<AdminOwnerLedger />} />
            <Route path="customizations" element={<AdminCustomizations />} />
            <Route path="settings" element={<AdminSettings />} />

            {/* New Reseller Data Routes */}
            <Route path="reseller-products" element={<AdminResellerProducts />} />
            <Route path="reseller-collections" element={<AdminResellerCollections />} />
            <Route path="reseller-categories" element={<AdminResellerCategories />} />

            {/* Funeral Supplies Routes */}
            <Route path="infinity-cases" element={<AdminInfinityCases />} />
            <Route path="infinity-orders" element={<AdminInfinityOrders />} />
            <Route path="infinity-price-multiplier" element={<AdminInfinityPriceMultiplier />} />
            <Route path="infinity-login-slider" element={<AdminInfinityLoginSlider />} />
            <Route path="infinity-app-version" element={<AdminInfinityAppVersion />} />
            <Route path="funeral-store/products" element={<AdminFuneralStoreProducts />} />
            <Route path="funeral-store/collections" element={<AdminFuneralStoreCollections />} />

            {/* Content Pages */}
            <Route path="website-content" element={<AdminWebsiteContent />} />
              <Route path="pages" element={<AdminContentPages />} />
            <Route path="pages/new" element={<AdminContentPageEditor />} />
            <Route path="pages/edit/:id" element={<AdminContentPageEditor />} />
          </Routes>
          )}
        </div>
      </main>

      {/* Profile Edit Drawer */}
      <Sheet open={showProfileModal} onOpenChange={setShowProfileModal}>
        <SheetContent side="right" className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader className="border-b pb-4 mb-6">
            <SheetTitle className="flex items-center gap-2 text-xl font-bold">
              <User className="w-6 h-6 text-primary" />
              Edit Admin Profile
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6">
            {/* Header Info */}
            <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border-2 border-primary/20">
                <span className="text-primary font-bold text-2xl">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <h4 className="font-bold text-slate-900">{user?.name || "Admin"}</h4>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <span className="inline-block px-2 py-0.5 bg-amber-200 text-amber-900 text-[10px] uppercase font-bold rounded mt-1">Platform Admin</span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                    className="pl-10 h-11 focus:ring-amber-500"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="admin@example.com"
                    className="pl-10 h-11 focus:ring-amber-500"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={profileData.password}
                    onChange={(e) => setProfileData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter new password"
                    className="pl-10 pr-10 h-11 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded border border-dashed">Leave blank to keep your current password secure.</p>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-11"
              onClick={() => setShowProfileModal(false)}
            >
              <X className="w-4 h-4 mr-2" />
              Discard
            </Button>
            <Button
              className="flex-1 h-11 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/20"
              onClick={handleSaveProfile}
              disabled={savingProfile}
            >
              {savingProfile ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
